import { Mode as ThemeMode } from '@scaleaq/scaleui';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { i18nInstance, Languages } from '@/localization';

import { storeNames } from './config/config.type.ts';

export interface UserSettingsState {
  theme: ThemeMode;
  language: Languages;
  enablePreviewContent: boolean;
}

interface UserSettingsStore extends UserSettingsState {
  /** Per-user (per-browser) preference for HLS rewind (DVR). Local-only — not synced to the
      user-settings API — so it is intentionally excluded from {@link UserSettingsState}. */
  enableVideoRecall: boolean;
  setTheme: (theme: ThemeMode) => void;
  setLanguage: (language: Languages) => void;
  setEnablePreviewContent: (value: boolean) => void;
  setEnableVideoRecall: (value: boolean) => void;
  syncFromApi: (settings: UserSettingsState) => void;
}

const getPersistedState = (): Partial<UserSettingsState> => {
  try {
    const raw = localStorage.getItem('userSettings');
    if (raw) return (JSON.parse(raw) as { state: UserSettingsState }).state ?? {};
  } catch {
    // ignore
  }
  return {};
};

const persisted = getPersistedState();

const defaults: UserSettingsState = {
  theme: 'auto',
  language: Languages.English,
  enablePreviewContent: false,
  ...persisted,
};

// Initialize i18n synchronously to avoid language flash on reload
if (persisted.language) {
  void i18nInstance.changeLanguage(persisted.language);
}

export const useUserSettingsStore = create(
  persist<UserSettingsStore>(
    set => ({
      ...defaults,
      // Local-only, defaults on; rehydrated from localStorage by persist. syncFromApi never
      // sets this key, so the API sync can't clobber it.
      enableVideoRecall: true,
      setTheme: (theme: ThemeMode) => {
        set({ theme });
      },
      setLanguage: (language: Languages) => {
        void i18nInstance.changeLanguage(language);
        set({ language });
      },
      setEnablePreviewContent: (value: boolean) => set({ enablePreviewContent: value }),
      setEnableVideoRecall: (value: boolean) => set({ enableVideoRecall: value }),
      syncFromApi: (settings: UserSettingsState) => {
        void i18nInstance.changeLanguage(settings.language);
        set(settings);
      },
    }),
    {
      name: storeNames.userSettings,
      storage: createJSONStorage(() => localStorage),
    }
  )
);
